import type { Palette } from './types';

type AboutProps = { palette: Palette };

export function AboutSection({ palette }: AboutProps) {
  return (
    <section id="about" className="relative px-6 pb-20 pt-10 scroll-mt-24 md:scroll-mt-28 lg:px-12">
      {/* Decorative background element connecting to previous section */}
      <div 
        className="absolute left-1/2 top-0 h-64 w-3/4 -translate-x-1/2 rounded-full opacity-20 blur-[100px]"
        style={{ background: `linear-gradient(90deg, ${palette.sage}, ${palette.clay})` }}
      />

      <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[40px] border border-slate-100 bg-white p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] lg:p-12">
        
        {/* Subtle top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-500 via-amber-400 to-emerald-500 opacity-80" />

        <div className="grid gap-12 lg:grid-cols-[1.1fr,0.9fr] lg:items-center">
          
          {/* Left: The Narrative */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-widest text-emerald-800">
               <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
               Our Purpose
            </div>

            <h2 className="text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
              We built the team you <br />
              <span className="relative inline-block">
                <span className="relative z-10 text-emerald-800">actually want</span>
                <span className="absolute bottom-1 left-0 right-0 h-3 -rotate-1 bg-amber-200/60 -z-0"></span>
              </span>
              {' '}to work with.
            </h2>
            
            <p className="text-lg leading-relaxed text-slate-600">
              We realized that for most homeowners, the hardest part isn't paying for the work—it's <span className="font-semibold text-slate-800">managing the chaos.</span>
              You don't want to play referee between a roofer, a plumber, and an electrician.
            </p>
            
            <p className="text-lg leading-relaxed text-slate-600">
              So we designed Home Pros to be the "easy button." One phone call, one project lead, and one cohesive plan. 
              We handle the logistics so you can get back to your real life.
            </p>
            
            <div className="pt-4 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-emerald-50 hover:text-emerald-800 hover:border-emerald-100">
                <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                No "middleman" markups
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-emerald-50 hover:text-emerald-800 hover:border-emerald-100">
                <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                Native Texas support
              </span>
            </div>
          </div>

          {/* Right: The Concrete Solutions (Interactive Cards) */}
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { 
                label: 'One Point of Contact', 
                sub: 'No passing the buck.',
                icon: (
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                ),
                color: palette.forest
              },
              { 
                label: 'Proactive Updates', 
                sub: 'We call you before you call us.',
                icon: (
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                ),
                color: palette.clay
              },
              { 
                label: 'Respectful Crews', 
                sub: 'We treat your home like ours.',
                icon: (
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                ),
                color: palette.sage
              },
              { 
                label: 'Texas Tough Specs', 
                sub: 'Materials built for our climate.',
                icon: (
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                ),
                color: '#D97706' // Hardcoded amber for pop
              }
            ].map((item, i) => (
              <div
                key={item.label}
                className="group relative flex flex-col justify-center rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-transparent hover:shadow-xl"
              >
                {/* Hover Gradient Background */}
                <div className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-slate-50 to-white opacity-0 transition-opacity group-hover:from-emerald-50/50 group-hover:to-white group-hover:opacity-100" />
                
                <div 
                  className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-500 transition-colors group-hover:scale-110 group-hover:text-white group-hover:shadow-md"
                  style={{ '--hover-color': item.color } as any}
                >
                  {/* We apply the color dynamically on hover using a little inline style trick or conditional classes. 
                      Here I'll use a direct style application for the icon wrapper on hover via parent group.
                  */}
                  <div className="text-slate-400 transition-colors group-hover:text-emerald-600">
                    {item.icon}
                  </div>
                </div>
                
                <span className="text-sm font-bold text-slate-900 group-hover:text-emerald-900">{item.label}</span>
                <span className="mt-1 text-xs font-medium text-slate-500 group-hover:text-slate-600">{item.sub}</span>
              </div>
            ))}
          </div>
          
        </div>
      </div>
    </section>
  );
}
